import {
  Container,
  Grid,
  makeStyles,
  Typography,
  Switch,
} from "@material-ui/core";
import { useEffect, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useSelector } from "react-redux";
import {
  CircularProggressIndicator,
  ShopRegionHeadline,
} from "../components-atoms";
import { LoadMoreIndicator } from "../components-atoms/LoadMoreIndicator";
import { InfoCard } from "../components-molecules/InfoCard";
import { NoResultsFound } from "../components-molecules/NoResultsFound";
import { ITheme } from "../constants/theme";
import { bottomText } from "../utils/promo-code/bottomText";
import { groupHeaders } from "../utils/promo-code/groupHeaders";
import { useTranslation } from 'react-i18next';

export interface IReferralCode {
  Id: string;
  Created: Date;
  Expires: Date;
  StartDate: Date;
  MinimumSpend: number | null;
  Code: string;
  Amount: number;
  Percentage: number;
  PromoCodeType: string;
  DiscountType: string;
  UseType: string;
  CreatedFor?: string;
  Active: boolean;
  FirstName?: string;
  LastName?: string;
  Email?: string;
}

interface IProps {
  sortType: string;
  query: string;
  filter: any;
  loadedCount: number;
  loadMore: () => void;
  onDeactivate: (id: string) => void;
  onReactivate: (item: IReferralCode) => void;
}

export const ReferralCodeList = ({
  sortType,
  query,
  filter,
  loadedCount,
  loadMore,
  onDeactivate,
  onReactivate
}: IProps) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [codes, loading]: [any, boolean] = useSelector((state: any) => [
    state.ReferralCodesSymbolsReducer.data as any,
    state.ReferralCodesSymbolsReducer.loading,
  ]);

  const filtersApplied = () => {
    let applied = false;

    Object.keys(filter).forEach((x: any, index: number) => {
      if (x === "MinimumSpend" || x === "Status") {
        if (filter[x]) {
          applied = true;
        }
      } else if (filter[x].length > 0) {
        applied = true;
      }
    });
    return applied;
  };
  const [filterApplied, setFilterApplied] = useState(filtersApplied());

  useEffect(() => {
    const result = filtersApplied();
    setFilterApplied(result);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  if (!codes || (!codes?.Data?.length && loading)) {
    return <CircularProggressIndicator />;
  }

  if (codes?.length === 0) {
    return <NoResultsFound query={query} filterApplied={filterApplied} />;
  }

  return (
    <InfiniteScroll
      style={{ overflow: "hidden" }}
      dataLength={codes?.Data?.length}
      next={loadMore}
      hasMore={loadedCount < codes.Count}
      scrollThreshold={0.99}
      loader={<LoadMoreIndicator />}
    >
      <Container className={classes.component}>
        {codes?.Data && codes?.Data?.length === 0 ? (
          <NoResultsFound query={query} filterApplied={filterApplied} />
        ) : (
          codes?.Data.map((sortedItem: any, index) => (
            <div key={sortedItem.GroupName}>
              {sortedItem.ReferralCodes?.length > 0 && (
                <ShopRegionHeadline
                  data-automation="number-of-referral-codes"
                  headlineTypeSingular="Referral Code"
                  headLineTypePlural="Referral Codes"
                  data={{
                    header: groupHeaders[sortType](sortedItem.GroupName),
                    count: sortedItem.ReferralCodes.length,
                  }}
                />
              )}
              {sortedItem.ReferralCodes.map((item: IReferralCode) => (
                <InfoCard
                  key={item.Id}
                  data={{
                    ...item,
bottomText: bottomText(item, t('currencySymbol')),
                    topText: `${item.Code}`,
                  }}
                  secondGrid={8}
                  thirdGrid={4}
                  disabled={true}
                >
                  <Grid container justifyContent="flex-end" alignItems="center">
                    <Typography
                      data-automation="referral-code-status"
                      variant="body2"
                      className={
                        item.Active ? classes.active : classes.archived
                      }
                      component="span"
                    >
                      {item.Active ? "Active" : "Archived"}
                    </Typography>
                    <Switch
                      color="primary"
                      checked={item.Active}
                      onChange={(e) => {
                        if (item.Active) {
                          onDeactivate(item.Id);
                          return;
                        }

                        onReactivate(item);
                      }}
                    />
                  </Grid>
                </InfoCard>
              ))}
            </div>
          ))
        )}
      </Container>
    </InfiniteScroll>
  );
};

const useStyles = makeStyles((theme: ITheme) => ({
  component: {
    display: "flex",
    flexDirection: "column",
    paddingLeft: 0,
    paddingRight: 0,
    marginTop: 24,
  },
  noRecordsFound: {
    display: "flex",
    paddingLeft: "24px",
    flexDirection: "column",
  },
  active: {
    color: theme.palette.success.main,
    fontWeight: 700,
    paddingRight: 12,
  },
  archived: {
    color: theme.palette.error.main,
    fontWeight: 700,
    paddingRight: 12,
  },
  deactivateButton: {
    marginLeft: 12,
  },
}));
